import { Component, EventEmitter, Input, Output, inject } from '@angular/core'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { IEmployerUser } from '@cnect/user-shared'
import { AssistantGateService } from '@employer/app/modules/assistant/services/assistant-gate.service'
import { GuideService } from '@employer/app/modules/assistant/services/guide.service'
import { ROUTES } from '@employer/app/services/navigation.service'
import { selectors } from '@employer/app/store/selectors'
import { AppConfigService } from '@engineering11/tenant-bootstrap-web'
import { isNotNil } from '@engineering11/utility'
import { ViewportService } from '@engineering11/web-utilities'
import { Store } from '@ngrx/store'
import { combineLatest, of } from 'rxjs'
import { Observable } from 'rxjs/internal/Observable'
import { filter, map, startWith, switchMap, tap } from 'rxjs/operators'
import { Crumb, buildBreadcrumbs, pathOf } from '@employer/app/services/breadcrumbs.util'
import { RecordTitleService } from '@employer/app/services/record-title.service'

@Component({
  selector: 'app-header',
  template: `
    <header class="e11-bg-skin-white e11-border-b e11-border-skin-app-borders e11-px-6 e11-py-2 e11-sticky e11-z-[12]">
      <div class="app-header-content e11-flex e11-justify-between ">
        <div class="e11-flex e11-w-full">
          <ng-container *ngrxLet="backUrl$ as backUrl">
            <ng-container *ngrxLet="backText$ as backText">
              <ng-container *ngrxLet="isMobile$ as isMobile">
                <!-- min-w-0 down the chain lets the trail truncate rather than push the account
                     block off the right edge; the block itself never shrinks (shrink-0 below). -->
                <div class="e11-grow e11-min-w-0 e11-flex e11-items-center">
                  <mobile-drawer-icon
                    [mobileDrawerState]="mobileDrawerState"
                    (mobileDrawerStateChange)="toggleMobileSideDrawer()"
                  ></mobile-drawer-icon>
                  <ng-container *ngrxLet="viewTitle$ as viewTitle">
                    <div class="e11-min-w-0 e11-flex-1">
                      <ng-container *ngrxLet="headerLogo$ as headerLogo">
                        <img
                          *ngIf="!isMobile && headerLogo"
                          alt="Cnect Logo"
                          src="/assets/images/maintenance-page/cnect-logo.png"
                          class=""
                          width="150"
                        />
                        <!--
                          Openings and Engage each collapsed to a single nav leaf, with the leaves
                          they used to hold now tabs on the screen — so this bar is the only place
                          the hierarchy is still written down. Section > tab > record, with every
                          crumb but the last a link back.
                        -->
                        <!-- The trail stays down to 'md', not 'lg': from 768 to 1023 the menu is in the
                             drawer but the settings list is still a docked column, so the trail is still
                             the only place the path is written. It goes when the list turns into the
                             dropdown under this bar. -->
                        <nav
                          *ngIf="!(isPhone$ | async) && !headerLogo"
                          class="app-header-crumbs"
                          aria-label="Breadcrumb"
                          [attr.data-single]="(breadcrumbs$ | async)?.length === 1 ? '' : null"
                        >
                          <ng-container *ngFor="let crumb of breadcrumbs$ | async; let last = last; let i = index">
                            <span class="material-symbols-outlined crumb-sep" *ngIf="i > 0" aria-hidden="true">chevron_right</span>
                            <a *ngIf="!last && crumb.link" class="crumb" [routerLink]="crumb.link" [queryParams]="crumb.queryParams ?? null">
                              {{ crumb.label | translate }}
                            </a>
                            <h2 *ngIf="last" class="app-header-title crumb-current e11-text-skin-base e11-line-clamp-1" aria-current="page">
                              {{ crumb.label | translate }}
                            </h2>
                          </ng-container>
                        </nav>
                        <!-- CNCT-6779: no screen title on mobile. The bar is narrow enough that the
                             title crowded the account block, and the screen names itself in its own
                             heading below. -->

                        <!--
                          The "Back to …" link used to sit here, on the same row as the title. The
                          breadcrumb trail now occupies that row and says the same thing with more
                          of it — every ancestor is clickable, not just the immediate parent — so
                          the link was redundant and in the way. Every route that set
                          showBackOnHeader pointed at a crumb the trail already renders
                          (candidates, openings, messages); that route data is left in place, inert,
                          rather than stripped out of four routing files.
                        -->
                      </ng-container>
                    </div>
                  </ng-container>
                </div>
              </ng-container>
            </ng-container>
          </ng-container>
          <div class="e11-flex e11-shrink-0 e11-ml-4 e11-items-center e11-justify-end">
            <div class="e11-flex e11-gap-6 e11-items-center e11-z-10">
              <!-- Standalone tour launcher (the "guide") — deliberately OUTSIDE the assistant dark-launch
                   flag: the tour ships on its own even where the assistant is off. Entitlement-gated:
                   shown to trial/Plus customers only, since a free customer's tour would half-work
                   (they get the upgrade path instead). -->
              <button
                *ngIf="(tourEntitled$ | async) && !(isMobile$ | async)"
                type="button"
                data-tour-id="header-tour-launcher"
                class="e11-w-9 e11-h-9 e11-rounded e11-flex e11-items-center e11-justify-center e11-text-skin-base hover:e11-bg-skin-primary/10 focus:e11-outline-none focus:e11-ring-2 focus:e11-ring-skin-primary-accent/60"
                [attr.aria-label]="'Take a tour' | translate"
                [title]="'Take a tour' | translate"
                (click)="startTour()"
              >
                <span class="material-icons-outlined md-24">explore</span>
              </button>
              <ui-notification *ngIf="currentUser$ | async as currentUser" [darkTheme]="true"></ui-notification>
              <!-- The bell's own library template hardcodes a 12px left margin, so the flex gap alone
                   leaves the cluster lopsided: 46px from the compass glyph across to the bell, but only
                   28px on from the bell to the account block, which has no inner inset of its own to
                   balance the bell's 4px padding. Matching that margin on this side evens the rhythm
                   up (44px vs 46px). The account block now leads with the name/company column and
                   closes on the avatar, so this margin spaces the bell from that text. -->
              <profile-dropdown *ngIf="currentUser$ | async as currentUser" class="e11-ml-4"></profile-dropdown>
            </div>
          </div>
        </div>
      </div>
    </header>
  `,
  styles: [
    `
      /* Breadcrumbs. The last crumb keeps .app-header-title, so the bar reads as it always did —
         it just gained the path above it. */
      .app-header-crumbs {
        container: crumbs / inline-size;
        display: flex;
        align-items: center;
        gap: 2px;
        min-width: 0;
      }

      /* When the bar is tight (1024 with the settings rail open) the account block keeps its width
         and the trail gives way: ancestors shrink first, so whatever room is left goes to the
         current page's name. */
      .app-header-crumbs a.crumb {
        flex: 0 100 auto;
        min-width: 0;
        max-width: 24ch;
        padding: 2px 3px;
        border-radius: 4px;
        font-size: 16.5px;
        color: rgb(var(--color-grey));
        text-decoration: none;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      /* Too tight for a readable path: show the current page alone rather than a sliver of an
         ancestor ahead of it. */
      @container crumbs (max-width: 180px) {
        .app-header-crumbs a.crumb,
        .app-header-crumbs .crumb-sep {
          display: none;
        }
      }

      .app-header-crumbs a.crumb:hover {
        color: rgb(var(--color-text-dark));
        text-decoration: underline;
      }

      .app-header-crumbs a.crumb:focus-visible {
        outline: 2px solid rgb(var(--color-primary-accent));
        outline-offset: 1px;
      }

      .app-header-crumbs .crumb-sep {
        flex: none;
        font-size: 17.6px;
        color: rgb(var(--color-grey));
        opacity: 0.55;
      }

      /* One size across the whole trail. The last crumb is an <h2> carrying
         .app-header-title (1.125rem), which set it a step above the links beside it and made the
         bar read as a title with small print in front of it rather than as one path. Both are now the
         mockup's current-crumb size (15px) scaled by 1.1; the current page stays distinguishable by
         weight and colour instead of by size. */
      .app-header-crumbs .crumb-current {
        margin: 0;
        min-width: 0;
        font-size: 16.5px;
        font-weight: 600;
        color: rgb(var(--color-text-dark));
      }

      /* ui-notification paints its bell e11-text-skin-primary-accent under [darkTheme], hard-coded
         in the library template, so the header's own chrome read as two colours. Match the
         hamburger and the account name at skin-base. The SVG strokes with currentColor, so the
         button's colour is all it takes. */
      :host ::ng-deep ui-notification button {
        color: rgba(var(--color-text-base), 1);
      }
      :host ::ng-deep ui-notification button:hover {
        color: rgba(var(--color-text-dark), 1);
      }

      /* The screen title renders as an h2, which the theme scale puts at 1.5rem/24px.
         18px is that same scale's $h4-font-size — the size the mobile variant of this
         title already uses — so desktop and mobile now match. Set here rather than per
         screen: every screen's title comes through this one element. */
      .app-header-title {
        font-size: 1.125rem;
      }
    `,
  ],
})
export class AppHeaderComponent {
  activatedRoute = inject(ActivatedRoute)
  currentUser$: Observable<IEmployerUser | null> = this.store.select(selectors.getCurrentUser)

  @Input() mobileDrawerState = false
  @Output() mobileDrawerStateChange: EventEmitter<boolean> = new EventEmitter<boolean>()
  isMobile$ = this.viewportService.viewportSizeChanged$.pipe(
    map(data => data == 'sm' || data == 'xs' || data == 'md'),
    filter(isNotNil),
    tap(isMobile => {
      if (!isMobile && this.mobileDrawerState) {
        this.toggleMobileSideDrawer()
      }
    })
  )
  /** Below 'md', where the settings list becomes a dropdown (see main-layout); hides the breadcrumb trail. */
  isPhone$ = this.viewportService.belowBreakpoint('md')
  logoUrl = this.appConfig.config.brand.logoImageDarkUrl
  routes = ROUTES

  combinedActivatedRoute$ = combineLatest([
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      startWith(this.router) // Handle the initial page load
    ),
    this.activatedRoute.root.firstChild!.params,
  ]).pipe(
    map(([_, params]) => {
      let route = this.activatedRoute.root
      while (route.firstChild) {
        route = route.firstChild
      }
      return route
    })
  )

  backUrl$ = this.combinedActivatedRoute$.pipe(
    switchMap(route => {
      const showBackOnHeader = route.snapshot.data['showBackOnHeader']
      if (!showBackOnHeader) return of('')
      if (route.snapshot.data['routeParams']) {
        return of(`${route.snapshot.data['backUrl']}/${route.snapshot.params['jobPostId']}`)
      } else {
        return of(`${route.snapshot.data['backUrl']}`)
      }
    })
  )

  backText$ = this.combinedActivatedRoute$.pipe(
    switchMap(route => {
      const showBackOnHeader = route.snapshot.data['showBackOnHeader']
      if (!showBackOnHeader) return of('')
      return of(`${route.snapshot.data['backText']}`)
    })
  )

  viewTitle$ = this.combinedActivatedRoute$.pipe(map(route => route.snapshot.data['title'] || ''))

  /**
   * The trail for the bar, recomputed from the URL on every navigation. On a record page it waits
   * for the page to publish the record (its name, and where it was reached from) for this URL, so
   * the trail appears once, complete, instead of changing as the record loads.
   */
  breadcrumbs$: Observable<Crumb[]> = combineLatest([this.combinedActivatedRoute$, this.recordTitle.record$]).pipe(
    map(([route, record]) => {
      const url = this.router.url
      const current = record?.path === pathOf(url) ? record : null
      return buildBreadcrumbs(url, route.snapshot.data['title'] as string | undefined, current)
    })
  )
  headerLogo$ = this.combinedActivatedRoute$.pipe(map(route => route.snapshot.data['headerLogo'] || ''))

  constructor(
    private viewportService: ViewportService,
    private appConfig: AppConfigService,
    private store: Store,
    private router: Router,
    private guide: GuideService,
    private assistantGate: AssistantGateService,
    private recordTitle: RecordTitleService
  ) {}

  /** The guided tour is a trial/paid feature (every company gets a 60-day trial). Once a company drops to
   *  free it loses the Cnect Plus entitlement and the nav changes, so the tour would half-work — hide the
   *  launcher entirely for free customers (they get the upsell/upgrade path instead of a broken tour). */
  tourEntitled$ = this.assistantGate.gate$.pipe(map(gate => gate === 'entitled'))

  toggleMobileSideDrawer() {
    this.mobileDrawerStateChange.emit(!this.mobileDrawerState)
  }

  /** Launch the floating guided walkthrough card on demand — the standalone "guide", independent of the chat. */
  startTour() {
    this.guide.start()
  }
}
